import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { AnswerPayload, QuestionSnapshot, ReviewAnswer, questionTypeLabel } from '../../../models/assignments';

interface AnswerViewProps {
  answer: ReviewAnswer;
  index: number;
}

// Индикатор автоматической проверки: true/false — выставлен бэкендом для choice-вопросов,
// null — вопрос не проверяется автоматически (free_text / file_upload) и ждёт ручной проверки.
const renderCorrectnessIndicator = (isCorrect: boolean | null): JSX.Element => {
  if (isCorrect === true) {
    return <Chip size="small" color="success" label="✓ верно" />;
  }
  if (isCorrect === false) {
    return <Chip size="small" color="error" label="✗ неверно" />;
  }
  return <Chip size="small" color="default" label="— требует проверки" />;
};

// Тело для вопросов с выбором варианта(ов): помечаем выбранные учеником и (если бэк прислал
// признак корректности хотя бы у одной опции) верные варианты.
const renderChoiceBody = (snapshot: QuestionSnapshot, payload: AnswerPayload): JSX.Element => {
  const selectedIds = payload.option_ids ?? [];
  // omitempty на бэке: is_correct может отсутствовать у всех опций (например, задание ещё не
  // настроено) — в этом случае пометку «верный» вообще не показываем.
  const hasCorrectInfo = snapshot.options.some((option) => option.is_correct !== undefined);

  return (
    <Box component="ul" sx={{ margin: 0, paddingLeft: 3 }}>
      {snapshot.options.map((option) => {
        const isSelected = selectedIds.includes(option.id);
        const isCorrectOption = hasCorrectInfo && option.is_correct === true;

        return (
          <Box component="li" key={option.id}>
            <Typography component="span" sx={{ fontWeight: isSelected ? 700 : 400 }}>
              {option.option_text}
            </Typography>
            {isSelected ? <Typography component="span"> (выбран)</Typography> : null}
            {isCorrectOption ? (
              <Typography component="span" sx={{ color: 'success.main' }}>
                {' '}
                (верный)
              </Typography>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
};

// Тело для свободного текста: сохраняем переносы строк через white-space: pre-wrap,
// текст не обрезается (до 10 000 символов по спецификации бэкенда).
const renderFreeTextBody = (payload: AnswerPayload): JSX.Element => {
  const text = payload.text ?? '';

  if (!text) {
    return <Typography>(пусто)</Typography>;
  }

  return (
    <Typography data-testid="answer-free-text-body" sx={{ whiteSpace: 'pre-wrap' }}>
      {text}
    </Typography>
  );
};

// Тело для загруженного файла: ссылка на реальный файл (если бэк её отдал), иначе — только
// ключ файла в хранилище и пометка о недоступности (например, файл ещё не прогрузился).
const renderFileUploadBody = (answer: ReviewAnswer): JSX.Element => {
  if (answer.file_url) {
    return (
      <a href={answer.file_url} target="_blank" rel="noopener noreferrer">
        Открыть файл
      </a>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography component="span">{answer.answer.file_key ?? ''}</Typography>
      <Chip size="small" label="файл недоступен" />
    </Box>
  );
};

const renderBody = (answer: ReviewAnswer): JSX.Element => {
  const { type } = answer.question_snapshot;

  if (type === 'single_choice' || type === 'multiple_choice') {
    return renderChoiceBody(answer.question_snapshot, answer.answer);
  }
  if (type === 'free_text') {
    return renderFreeTextBody(answer.answer);
  }
  return renderFileUploadBody(answer);
};

const AnswerView: React.FC<AnswerViewProps> = ({ answer, index }) => {
  return (
    <Box sx={{ marginBottom: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1">
          {index + 1}. {answer.question_snapshot.question_text}
        </Typography>
        <Chip size="small" label={questionTypeLabel(answer.question_snapshot.type)} />
        {answer.question_id === null ? (
          <Chip size="small" color="warning" label="вопрос удалён из задания" />
        ) : null}
        {renderCorrectnessIndicator(answer.is_correct)}
      </Box>
      <Box sx={{ marginTop: 1 }}>{renderBody(answer)}</Box>
    </Box>
  );
};

export { AnswerView };
export default AnswerView;
